import { Box, IconButton, Typography } from "@mui/material"
import { CarModelGeneralInfo } from "api"
import NextLink from "common/bit/NextLink"
import { buildShopLink } from "common/buildShopLink"
import MoreInfoWhite from "reusable/icons/MoreInfoWhite"
import { useTypedSelector } from "store"
import styled, { keyframes } from "styled-components"
import { TypographyCenter } from "theme/shared"

const StyledFooterDiv = styled.div`
  height: 190px;
  display: flex;
  flex-direction: column;
  margin: 0px auto auto;
  width: 270px;
  justify-content: center;
  align-items: center;
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-bottom: 0px;
  }
`
const StyledPriceTitle = styled(TypographyCenter).attrs({ variant: "body1" })`
  font-size: 14px;
  color: ${(p) => p.theme.colors.onyx};
`

const StyledPrice = styled(Typography).attrs({ variant: "body1" })`
  font-family: MazdaType, OENaPro, sans-serif;
  font-size: 24px;
  text-align: center;
`

const StyledPriceWrapper = styled.div<{ $justify: string }>`
  min-width: 270px;
  justify-content: ${(p) => p.$justify};
  display: flex;
  flex-direction: row;
  margin-bottom: 10px;
`

const StyledPriceLine = styled.div`
  width: 1px;
  height: 62px;
  background: ${(p) => p.theme.colors.nonExistentLightGray};
  transform: rotate(29.19deg);
`

const StyledLicenseFee = styled(Typography).attrs({ variant: "body2" })`
  font-size: 14px;
  color: ${(p) => p.theme.colors.black};
  text-align: center;
  margin: 0 auto;
`

const StyledPurchase = styled(Typography).attrs({ variant: "body2" })`
  text-align: center;

  margin-top: 4px;
  height: 23px;
  color: ${(p) => p.theme.colors.mazdaRed};
  font-size: 14px;
  font-weight: normal;
`

const StyledDiv = styled.div<{
  $marginTop?: number
  $marginBottom?: number
  $cursor?: string
}>`
  ${(p) => p.$marginTop && `margin-top: ${p.$marginTop}px;`}
  ${(p) => p.$marginBottom && `margin-bottom: ${p.$marginBottom}px;`}
  ${(p) => p.$cursor && `cursor: ${p.$cursor}`}
`

const arrowDown = keyframes`
  0%,
	100% {
		transform: translateY(0);
	}
	8%,
	24%,
	40%,
	56% {
		transform: translateY(-2.5px);
	}
	16%,
	32%,
	48% {
		transform: translateY(2.5px);
	}
	64% {
		transform: translateY(2px);
	}
	72% {
		transform: translateY(-2px);
	}
  80%{
    transform: translateY(0);
  }
`

const StyledArrowDownWrap = styled(Box)`
  // add loop down animation to svg path with id arrow
  svg path#arrow {
    animation: ${arrowDown} 5s ease 0s infinite normal forwards;
  }
`
type ModelInfo = {
  carData: CarModelGeneralInfo
  openCalculator: () => void
  onScrollDownClick: () => void
}

const ModelInfo = ({
  carData,
  openCalculator,
  onScrollDownClick,
}: ModelInfo) => {
  const { id: carId } = carData
  const detailData = useTypedSelector((state) => state?.detailing)

  const { detailing, color } = detailData

  const shopUrl = buildShopLink(detailing, color, carId)

  const onArrowDownClick = (e) => {
    e.preventDefault()
    // scroll 170px down
    window?.scrollTo(0, 171)
    onScrollDownClick()
  }

  return (
    <StyledFooterDiv>
      <StyledPriceWrapper
        $justify={carData?.mountlyPayment ? "space-between" : "center"}
      >
        <StyledDiv>
          <StyledPriceTitle>החל מ-</StyledPriceTitle>
          <StyledPrice>₪{carData.price.toLocaleString()}</StyledPrice>
        </StyledDiv>
        {!!carData?.mountlyPayment && (
          <>
            <StyledPriceLine />
            <StyledDiv onClick={() => openCalculator()} $cursor="pointer">
              <StyledPriceTitle>החזר חודשי החל מ-</StyledPriceTitle>
              <StyledPrice>
                ₪
                {carData.mountlyPayment
                  ? carData.mountlyPayment.toLocaleString()
                  : 0}
              </StyledPrice>
            </StyledDiv>
          </>
        )}
      </StyledPriceWrapper>

      <StyledDiv $marginBottom={10}>
        {carData?.licenceFee > 0 && carData?.isInShop && (
          <StyledLicenseFee>
            בתוספת אגרת רישוי של ₪{carData.licenceFee.toLocaleString()} כולל
            מע״מ
          </StyledLicenseFee>
        )}
        {carData.typeId === 3 ? (
          <NextLink href={shopUrl}>
            <StyledPurchase>ניתן להזמנה מידית ממלאי עתידי</StyledPurchase>
          </NextLink>
        ) : (
          carData.isInShop && (
            <NextLink href={shopUrl}>
              <StyledPurchase>ניתן להזמנה מיידית אונליין</StyledPurchase>
            </NextLink>
          )
        )}
      </StyledDiv>
      <StyledArrowDownWrap>
        <IconButton
          onClick={onArrowDownClick}
          onKeyDown={onArrowDownClick}
          aria-label="מעבר לאיזור הבא בעמוד"
        >
          <MoreInfoWhite />
        </IconButton>
      </StyledArrowDownWrap>
    </StyledFooterDiv>
  )
}

export default ModelInfo
